// Editable item icons. PURE (ENG-1).
//
// TerraForge ships no image files — every texture is painted procedurally in
// code (render/textures/*). That is compact and asset-free, but it also means an
// item's look is unreachable from data, so the content editor could change an
// item's stats but not its appearance.
//
// An `icon` is the data-driven override: a small palette plus one character per
// pixel indexing into it. 16×16 with a ≤36-colour palette fits in ~300 bytes of
// JSON, stays diff-friendly in git, and needs no binary assets or build step.
// Absent `icon` ⇒ the item keeps its procedural sprite, as before.

/** Icon side length, matching the atlas tile size. */
export const ICON_SIZE = 16;
/** Hard palette cap: one base-36 character per pixel means 36 colours. */
export const MAX_PALETTE = 36;
/** Seeding stops here, leaving room to paint NEW colours. Without headroom a
 *  noisy source tile (grass is painted with 0.35 per-pixel jitter, so nearly
 *  every texel differs) fills the palette and every colour you pick afterwards
 *  snaps to the nearest existing one — i.e. it appears to paint the old colour. */
export const SEED_PALETTE = 24;
/** Marks a transparent pixel (the procedural sprites are mostly transparent). */
export const TRANSPARENT = '.';

export interface IconData {
  /** Up to MAX_PALETTE '#rrggbb' colours. */
  palette: string[];
  /** ICON_SIZE² characters, row-major. Each is a base-36 palette index, or '.'. */
  pixels: string;
}

/** Palette index → its character, and back. Base 36 keeps the palette to one
 *  character per pixel, so the string length is exactly the pixel count. */
export function indexToChar(i: number): string {
  return i.toString(36);
}
export function charToIndex(c: string): number {
  return c === TRANSPARENT ? -1 : parseInt(c, 36);
}

export function isBlank(icon: IconData): boolean {
  return !icon.pixels || [...icon.pixels].every((c) => c === TRANSPARENT);
}

/** Structural check — used by the schema's refine and by the editor. */
export function isValidIcon(icon: IconData): boolean {
  if (!Array.isArray(icon.palette) || icon.palette.length === 0 || icon.palette.length > MAX_PALETTE) return false;
  if (typeof icon.pixels !== 'string' || icon.pixels.length !== ICON_SIZE * ICON_SIZE) return false;
  for (const c of icon.pixels) {
    if (c === TRANSPARENT) continue;
    const i = charToIndex(c);
    if (!Number.isInteger(i) || i < 0 || i >= icon.palette.length) return false;
  }
  return true;
}

/** RGBA bytes (ICON_SIZE² × 4) → an icon, quantised to leave palette headroom.
 *  Used to seed the editor from whatever the item renders as today, so editing
 *  starts from the existing sprite rather than a blank square. */
export function iconFromRgba(rgba: Uint8ClampedArray): IconData {
  // Procedural tiles carry per-pixel noise, so the raw colour count is close to
  // the pixel count. Round each channel to a coarser step until the distinct
  // colours fit with headroom — visually near-identical, and it leaves palette
  // slots free for the colours the author is about to paint.
  for (const step of [1, 2, 4, 8, 16, 32, 64]) {
    const attempt = quantise(rgba, step);
    if (attempt.palette.length <= SEED_PALETTE || step === 64) return attempt;
  }
  return quantise(rgba, 64); // unreachable, but keeps the return type honest
}

/** One quantisation pass at a given channel step. */
function quantise(rgba: Uint8ClampedArray, step: number): IconData {
  const palette: string[] = [];
  const index = new Map<string, number>();
  let pixels = '';
  for (let p = 0; p < ICON_SIZE * ICON_SIZE; p++) {
    const a = rgba[p * 4 + 3] ?? 0;
    if (a < 8) { pixels += TRANSPARENT; continue; }
    const hex = rgbToHex(
      snap(rgba[p * 4] ?? 0, step), snap(rgba[p * 4 + 1] ?? 0, step), snap(rgba[p * 4 + 2] ?? 0, step),
    );
    let i = index.get(hex);
    if (i === undefined) {
      if (palette.length >= MAX_PALETTE) {
        i = nearestIndex(palette, hex);
      } else {
        i = palette.length;
        palette.push(hex);
        index.set(hex, i);
      }
    }
    pixels += indexToChar(i);
  }
  if (palette.length === 0) palette.push('#000000'); // a palette is never empty
  return { palette, pixels };
}

/** Round a channel to the nearest multiple of `step`, staying in range. */
function snap(v: number, step: number): number {
  if (step <= 1) return v;
  return Math.min(255, Math.round(v / step) * step);
}

/** Drop palette entries no pixel references any more, reindexing what remains.
 *  Editing over a seeded sprite orphans colours quickly; reclaiming them is what
 *  keeps a newly picked colour from having to snap to an existing one. */
export function compactPalette(icon: IconData): IconData {
  const used = new Set<number>();
  for (const c of icon.pixels) {
    const i = charToIndex(c);
    if (i >= 0) used.add(i);
  }
  if (used.size === icon.palette.length) return icon;
  const remap = new Map<number, number>();
  const palette: string[] = [];
  for (let i = 0; i < icon.palette.length; i++) {
    if (!used.has(i)) continue;
    remap.set(i, palette.length);
    palette.push(icon.palette[i]!);
  }
  if (palette.length === 0) palette.push('#000000');
  let pixels = '';
  for (const c of icon.pixels) {
    const i = charToIndex(c);
    pixels += i < 0 ? TRANSPARENT : indexToChar(remap.get(i) ?? 0);
  }
  return { palette, pixels };
}

/** Icon → RGBA bytes, for painting onto a canvas. */
export function iconToRgba(icon: IconData): Uint8ClampedArray {
  const out = new Uint8ClampedArray(ICON_SIZE * ICON_SIZE * 4);
  for (let p = 0; p < ICON_SIZE * ICON_SIZE; p++) {
    const i = charToIndex(icon.pixels[p] ?? TRANSPARENT);
    if (i < 0) continue;
    const [r, g, b] = hexToRgb(icon.palette[i] ?? '#000000');
    out[p * 4] = r; out[p * 4 + 1] = g; out[p * 4 + 2] = b; out[p * 4 + 3] = 255;
  }
  return out;
}

/** Set one pixel, adding the colour to the palette if it is new. Returns a new
 *  icon; `null` colour erases. */
export function setPixel(icon: IconData, x: number, y: number, color: string | null): IconData {
  if (x < 0 || y < 0 || x >= ICON_SIZE || y >= ICON_SIZE) return icon;
  const p = y * ICON_SIZE + x;
  let base = icon;
  let ch = TRANSPARENT;
  if (color !== null) {
    const hex = color.toLowerCase();
    let i = base.palette.indexOf(hex);
    if (i < 0 && base.palette.length >= MAX_PALETTE) {
      base = compactPalette(base); // reclaim orphaned colours before giving up
      i = base.palette.indexOf(hex);
    }
    if (i < 0) {
      if (base.palette.length >= MAX_PALETTE) {
        i = nearestIndex(base.palette, hex); // genuinely full — closest match
      } else {
        base = { palette: [...base.palette, hex], pixels: base.pixels };
        i = base.palette.length - 1;
      }
    }
    ch = indexToChar(i);
  }
  return { palette: base.palette, pixels: base.pixels.slice(0, p) + ch + base.pixels.slice(p + 1) };
}

export function blankIcon(): IconData {
  return { palette: ['#000000'], pixels: TRANSPARENT.repeat(ICON_SIZE * ICON_SIZE) };
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16) || 0, parseInt(h.slice(2, 4), 16) || 0, parseInt(h.slice(4, 6), 16) || 0];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const p = (n: number): string => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${p(r)}${p(g)}${p(b)}`;
}

/** Closest palette entry by squared RGB distance (palette-full fallback). */
function nearestIndex(palette: readonly string[], hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  let best = 0, bestD = Infinity;
  for (let i = 0; i < palette.length; i++) {
    const [pr, pg, pb] = hexToRgb(palette[i]!);
    const d = (pr - r) ** 2 + (pg - g) ** 2 + (pb - b) ** 2;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

/** Cube-face names in atlas-table order (mesh/shapes/cube.ts CUBE_FACES):
 *  0 −X, 1 +X, 2 −Y, 3 +Y, 4 −Z, 5 +Z. */
export const FACE_NAMES = ['west', 'east', 'bottom', 'top', 'north', 'south'] as const;
export type FaceName = (typeof FACE_NAMES)[number];

/** A block's painted skins: one per face, with `all` as the fallback. */
export type BlockTexture = Partial<Record<'all' | FaceName, IconData>>;

/** The icon a given face index should draw, or null to keep the original. */
export function faceIcon(tex: BlockTexture | undefined, face: number): IconData | null {
  if (!tex) return null;
  return tex[FACE_NAMES[face] ?? 'all'] ?? tex.all ?? null;
}
